import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useQueryBuilderOverlay } from '../context/QueryBuilderOverlayContext';
import { useImageToolsOverlay } from '../context/ImageToolsOverlayContext';
import { useConeSearchOverlay } from '../context/ConeSearchOverlayContext';
import { useQueryHistoryOverlay } from '../context/QueryHistoryOverlayContext';
import { getImageCollections } from '../api/images';

const ToolCard = ({ title, description, icon, to, requiresAuth = false, comingSoon = false }) => {
  const { isAuthenticated } = useAuth();
  
  // If tool requires auth and user is not authenticated, don't show it
  if (requiresAuth && !isAuthenticated) return null;
  
  // Base classes for both clickable and coming-soon cards
  const baseCardClasses =
    "relative flex flex-col items-center p-6 rounded-2xl " +
    "border border-white/10 dark:border-white/10 " +
    "bg-black/5 dark:bg-white/5 " +                // subtle transparent bg
    // make bg translucent with blur
    "backdrop-blur-md " +                          // blur effect
    "shadow-sm transition-all";

  // If tool is coming soon, render a non-clickable version
  if (comingSoon) {
    return (
      <div
        className={
          baseCardClasses +
          " opacity-80 cursor-default"
        }
      >
        <div className="absolute top-2 right-2 bg-amber-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">
          Coming soon
        </div>

        <div className="p-4 mb-4 rounded-full bg-white/5 dark:bg-black/20 text-primary-400">
          {icon}
        </div>

        <h3 className="mb-2 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
          {title}
        </h3>
        <p className="text-center text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
          {description}
        </p>
      </div>
    );
  }
  
  // Regular clickable card
  return (
    <Link
      to={to}
      className={
        baseCardClasses +
        " group hover:border-primary-400/70 hover:bg-white/10 " +
        "hover:-translate-y-0.5 hover:shadow-md"
      }
    >
      <div className="p-4 mb-4 rounded-full bg-white/5 dark:bg-black/30 text-primary-400 group-hover:bg-primary-500 group-hover:text-white transition-colors">
        {icon}
      </div>

      <h3 className="mb-2 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary group-hover:text-primary-400 transition-colors">
        {title}
      </h3>

      <p className="text-center text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
        {description}
      </p>
    </Link>
  );
};

const Tools = () => {
  const { isAuthenticated, isStaff, isSuperuser } = useAuth();
  const { isOpen: isQueryOpen, openOverlay: openQueryOverlay, closeOverlay: closeQueryOverlay } = useQueryBuilderOverlay();
  const { isOpen: isImageToolsOpen, openOverlay: openImageToolsOverlay, closeOverlay: closeImageToolsOverlay } = useImageToolsOverlay();
  const { isOpen: isConeSearchOpen, openOverlay: openConeSearchOverlay, closeOverlay: closeConeSearchOverlay } = useConeSearchOverlay();
  const { isOpen: isQueryHistoryOpen, openOverlay: openQueryHistoryOverlay, closeOverlay: closeQueryHistoryOverlay } = useQueryHistoryOverlay();
  const [firstCollectionId, setFirstCollectionId] = useState(null);

  useEffect(() => {
    const fetchCollections = async () => {
      try {
        const collections = await getImageCollections();
        if (collections && collections.length > 0) {
          setFirstCollectionId(collections[0].id);
        }
      } catch (error) {
        console.error('Error fetching collections:', error);
      }
    };
    fetchCollections();
  }, []);

  const handleToggleToolsOverlay = () => {
    const anyOpen = isQueryOpen || isImageToolsOpen || isConeSearchOpen || isQueryHistoryOpen;
    if (anyOpen) {
      closeQueryOverlay();
      closeImageToolsOverlay();
      closeConeSearchOverlay();
      closeQueryHistoryOverlay();
    } else {
      openQueryOverlay({ minimized: true });
      openConeSearchOverlay({ minimized: true });
      openQueryHistoryOverlay({ minimized: true });
      if (firstCollectionId) {
        openImageToolsOverlay({ collectionId: firstCollectionId, minimized: true });
      }
    }
  };
  
  // Define all possible tools
  const tools = [
    {
      title: "Dashboard",
      description: "Recent activity, saved queries and quick links.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
        </svg>
      ),
      to: "/dashboard",
      requiresAuth: true
    },
    {
      title: "Query",
      description: "Write and run astroql / ADQL on the portal datasets.",
      icon: (
        // </> style code icon
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 17L3 12l5-5M16 7l5 5-5 5M14 4l-4 16"
          />
        </svg>
      ),
      to: "/query",
      requiresAuth: false
    },
    {
      title: "Create Prompt",
      description: "Assemble docs, tables and instructions into an LLM-ready prompt.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h8M8 14h5M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2h-3.586a1 1 0 01-.707-.293l-1.414-1.414A1 1 0 0012.586 3h-1.172a1 1 0 00-.707.293L9.293 4.707A1 1 0 018.586 5H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      ),
      to: "/create-prompt",
      requiresAuth: false
    },
    {
      title: "Query History",
      description: "Find and rerun your previous queries.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      to: "/queries/history",
      requiresAuth: true
    },
    {
      title: "Database Explorer",
      description: "Browse schemas, tables and columns.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4m0 5c0 2.21-3.582 4-8 4s-8-1.79-8-4" />
        </svg>
      ),
      to: "/database",
      requiresAuth: false
    },
    {
      title: "Cone Search",
      description: "Objects within a radius around a position.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
      to: "/cone-search",
      requiresAuth: false
    },
    {
      title: "Images",
      description: "Browse image cutouts and collections.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      to: "/images",
      requiresAuth: false
    },
    {
      title: "Interactive Viewer",
      description: "Aladin Lite sky viewer.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      to: "/interactive-viewer",
      requiresAuth: false
    },
    {
      title: "News",
      description: "Announcements and updates.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
        </svg>
      ),
      to: "/news",
      requiresAuth: true
    },
    {
      title: "Admin",
      description: "Administrative tools and settings.",
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      to: "/admin",
      requiresAuth: true,
      requiresAdmin: true
    }
  ];

  // Filter tools based on user permissions
  const filteredTools = tools.filter(tool => {
    if (tool.requiresAuth && !isAuthenticated) {
      return false;
    }
    if (tool.requiresAdmin && !(isStaff() || isSuperuser())) {
      return false;
    }
    return true;
  });

  return (
    <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
      <div className="mb-8 text-center">
        <h1 className="text-3xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">
          Astronomy Data Portal
        </h1>
        <p className="mt-2 text-lg text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
          Minimal tools to explore, query and visualize your data.
        </p>
      </div>

      {/* Tools Overlay Button */}
      <div className="flex justify-center mb-12">
        <button
          onClick={handleToggleToolsOverlay}
          className="relative group px-8 py-4 rounded-2xl border border-primary-400/30 bg-gradient-to-br from-primary-500/10 to-primary-600/5 dark:from-primary-500/20 dark:to-primary-600/10 backdrop-blur-md shadow-lg hover:shadow-xl hover:border-primary-400/50 transition-all hover:-translate-y-0.5"
        >
          <div className="flex items-center gap-3">
            <div className="p-3 rounded-full bg-primary-500/20 dark:bg-primary-500/30 group-hover:bg-primary-500/30 dark:group-hover:bg-primary-500/40 transition-colors">
              <svg className="w-6 h-6 text-primary-600 dark:text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <div className="text-left">
              <h3 className="text-lg font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                Tools Overlay
              </h3>
              <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-secondary">
                Open query and image tools on the right side
              </p>
            </div>
            <svg className="w-5 h-5 text-primary-600 dark:text-primary-400 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 mt-12 sm:grid-cols-2 lg:grid-cols-3">
        {filteredTools.map((tool, index) => (
          <ToolCard
            key={index}
            title={tool.title}
            description={tool.description}
            icon={tool.icon}
            to={tool.to}
            requiresAuth={tool.requiresAuth}
            comingSoon={tool.comingSoon}
          />
        ))}
      </div>
    </div>
  );
};

export default Tools;